/**
 * ログイン中ユーザーを解決するフック。
 *
 * ログイン導線は Edge Router Worker が所有するため、本フックはログインの mutation を持たない。
 * 未認証は API が 401 を返すので、呼び出し側はその error を掴んでログイン誘導に倒す。
 *
 * ログアウトも骨格フェーズでは持たない。サーバー側セッション失効の配線（API 側の
 * `auth.logout`）が入っていない段階で「ログアウト」ボタンを出すと、cookie が残ったまま
 * ログアウトしたつもりになる状態を作るため、API 実装と同時に足す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ログイン中ユーザーを oRPC 経由で取得し、認証状態を画面へ供給する。
 * @graph-connects rpcClient [calls] auth.me oRPC procedure
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
import { useQuery } from '@tanstack/react-query';
import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ログイン中ユーザー・認証状態・取得エラーを画面へ返す。
 * @graph-connects rpcClient [calls] auth.me oRPC procedure
 */
export function useAuth() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.auth.current,
    queryFn: () => rpcClient.auth.me(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const user = data?.user ?? null;

  return {
    user,
    isAuthenticated: user !== null,
    isLoading,
    error,
    refetch,
  };
}
